import { useMemo, useState } from "react";
import { Button, ButtonVariant, ButtonSize, Hide } from "shared/components";
import { formatToCurrency } from "shared/utils";
import { CriteriaRentType, Money } from "shared/types";
import { EditApplicationRentModal } from "./EditApplicationRentModal";
import { useAuthContext } from "providers/Auth";
import { AdjustedApplicationRentActivity } from "shared/app/ActivityLog/types";

interface ApplicationRentSectionProps {
  applicationId: string;
  applicationRentAmount: {
    monthly_rent_amount?: Money;
    rent_amount?: Money;
    home_rent_amount?: Money;
    site_rent_amount?: Money;
    previous_rent_amount?: Money;
    housing_assistance_voucher_amount?: Money;
    pet_rent_amount?: Money;
  };
  onViewDetails: () => void;
  canEdit?: boolean;
  onSuccess?: () => void;
  latestApplicationRentHistory?: AdjustedApplicationRentActivity;
  criteriaRentType: CriteriaRentType;
}

export const ApplicationRentSection = ({
  applicationId,
  applicationRentAmount,
  onViewDetails,
  canEdit = true,
  onSuccess,
  latestApplicationRentHistory,
  criteriaRentType
}: ApplicationRentSectionProps) => {
  const [showSetRentModal, setShowSetRentModal] = useState(false);
  const { pms } = useAuthContext();

  const latestRentHistoryString = useMemo(() => {
    let result = "";
    if (pms.isManageAmerica) {
      if (criteriaRentType !== CriteriaRentType.Site)
        result +=
          formatToCurrency(applicationRentAmount.home_rent_amount || null) +
          " home";
      if (criteriaRentType === CriteriaRentType.Both) result += " + ";
      if (criteriaRentType !== CriteriaRentType.Home)
        result +=
          formatToCurrency(applicationRentAmount.site_rent_amount || null) +
          " site";
    } else
      result +=
        formatToCurrency(applicationRentAmount.rent_amount || null) +
        " rent amount";

    if (applicationRentAmount.housing_assistance_voucher_amount?.cents) {
      result += " - ";
      result +=
        formatToCurrency(
          applicationRentAmount.housing_assistance_voucher_amount
        ) + " assistance";
    }

    if (applicationRentAmount.pet_rent_amount) {
      result += " + ";
      result +=
        formatToCurrency(applicationRentAmount.pet_rent_amount) + " pet";
    }

    return result;
  }, [
    latestApplicationRentHistory,
    applicationRentAmount,
    pms.isManageAmerica
  ]);

  return (
    <div className="w-full flex items-center gap-4">
      <div className="flex-1">
        <p className="text-sm font-medium text-content-1000 mb-2">
          Application rent
        </p>
        <div className="flex items-end gap-2 flex-wrap">
          <p className="text-base font-bold text-content-1000">
            {formatToCurrency(
              applicationRentAmount.monthly_rent_amount || null
            )}{" "}
            <span className=" text-content-300 text-xs font-medium">
              ({latestRentHistoryString})
            </span>
          </p>
          <Hide if={!latestApplicationRentHistory}>
            <span className="text-sm font-medium text-content-300">
              *adjusted.
            </span>

            <Button
              variant={ButtonVariant.TEXT}
              size={ButtonSize.SM}
              onClick={onViewDetails}
              className="!text-primary-500 !p-0 !text-sm"
            >
              View details
            </Button>
          </Hide>
        </div>
      </div>

      <Hide if={!canEdit}>
        <Button
          variant={ButtonVariant.BASE}
          size={ButtonSize.SM}
          onClick={() => setShowSetRentModal(true)}
        >
          Edit
        </Button>
      </Hide>

      <EditApplicationRentModal
        opened={showSetRentModal}
        onClose={() => setShowSetRentModal(false)}
        applicationId={applicationId}
        applicationRentAmount={applicationRentAmount}
        onSuccess={onSuccess}
        criteriaRentType={criteriaRentType}
      />
    </div>
  );
};
